<script setup lang="ts">
import ImageUploaderNew from '@/components/ui/ImageUploader/ImageUploaderNew.vue'
import { ComboboxSelect } from '@/ui/combobox'
import { useVModel } from '@vueuse/core'
import axios from 'axios'

import type { Icons } from '@/components/base/BaseIcon.vue'

import { addProductIfNotExists, groupPartnerAccounts, removeProductFromList } from '@/utils/productHelpers'
import {
  getRewardSummaryDetail,
  getRewardTypeIcon,
  getRewardTypeLabel,
  getRewardUsageTags,
  isRewardInputDisplayed
} from '@/components/rewards/utils/rewardHelpers.js'
import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'

import RewardInfosBlock from '@/components/rewards/RewardInfosBlock.vue'
import RewardTypeBlock from '@/components/rewards/RewardTypeBlock.vue'

import PartnerProductModal from './PartnerProductModal.vue'
import ProductAssociationBlock from './ProductAssociationBlock.vue'
import RewardFormBlock from './RewardFormBlock.vue'
import RewardUsageTag from './RewardUsageTag.vue'

import type { ImageFile, LoyaltyReward, Partner, PartnerParameter, PartnerProduct, RewardUsage } from '@/types/types'

const props = withDefaults(
  defineProps<{
    editingReward: LoyaltyReward
    usage?: RewardUsage
    sideitemTopOffset?: string
  }>(),
  {
    sideitemTopOffset: 'top-6'
  }
)

const emit = defineEmits<{
  (e: 'update:nameChanged', name: string): void
}>()

const editingReward = useVModel(props, 'editingReward')

const { t } = useI18n()
const router = useRouter()

const { rewardVouchers } = useRewardForm()

const showProductAssociation = computed(() => props.usage !== 'offer')

const rewardInfosBlockRef = ref<InstanceType<typeof RewardInfosBlock> | null>(null)

const REWARD_NAME_MAX_LENGTH = 50
const REWARD_DESCRIPTION_MAX_LENGTH = 145

const rewardDescription = computed({
  get: () => editingReward.value.description ?? '',
  set: (value: string) => {
    editingReward.value.description = value
  }
})

const errors = ref<string[]>([])
const nameContainer = ref<HTMLElement | null>(null)

const imageUploader = ref<InstanceType<typeof ImageUploaderNew> | null>(null)
const initialImages = ref<ImageFile[]>([])

const images = ref<ImageFile[]>([])
const deletedImages = ref<number[]>([])

const marketplacePartners = ref<PartnerParameter[]>([])
const isLoadingPartners = ref(false)

const isProductModalOpen = ref(false)
const selectedPartner = ref<Partner | null>(null)

const rewardPartners = computed(() => groupPartnerAccounts(marketplacePartners.value))

const usageTags = computed(() => {
  return getRewardUsageTags(editingReward.value.usages ?? [props.usage], t)
})

const rewardTypeLabel = computed(() =>
  editingReward.value?.rewardType ? getRewardTypeLabel(editingReward.value.rewardType, t) : ''
)

const rewardTypeIcon = computed(() =>
  editingReward.value?.rewardType ? getRewardTypeIcon(editingReward.value.rewardType) : 'HeroGiftIconOutline'
)

const rewardSummaryItems = computed(() => (editingReward.value ? getRewardSummaryDetail(editingReward.value, t) : []))

const isDisplayed = (slug = null) =>
  isRewardInputDisplayed(editingReward.value.rewardType.id, slug, rewardVouchers.value)

const setNameErrorState = () => {
  nameContainer.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  errors.value.push(t('pages.loyaltyPage.edit.fields.nameError'))
}

const resetErrors = () => {
  errors.value = []
}

const updateImages = (newImages: ImageFile[]) => {
  images.value = newImages

  if (newImages.length > 0) {
    const firstImage = newImages[0]
    editingReward.value.pictureUrl = firstImage.path
  } else {
    editingReward.value.pictureUrl = '/images/default-profile-picture.jpg'
  }
}

const updateDeletedImages = (deletedIds: number[]) => {
  deletedImages.value = deletedIds
}

const getMarketplacePartners = async () => {
  try {
    isLoadingPartners.value = true
    const { data } = await axios.get('/partner-parameters', {
      params: {
        hasRewardSupport: 1,
        isActive: 1
      }
    })
    marketplacePartners.value = data.data
  } catch {
    marketplacePartners.value = []
  } finally {
    isLoadingPartners.value = false
  }
}

const openProductModal = (partner: Partner) => {
  selectedPartner.value = partner
  isProductModalOpen.value = true
}

const handleProductSelect = (product: PartnerProduct) => {
  addProductIfNotExists(editingReward.value.items, product)
}

const removeAssociatedProduct = (product: PartnerProduct) => {
  removeProductFromList(editingReward.value.items, product)
}

const resetImage = () => {
  if (editingReward.value.pictureUrl) {
    initialImages.value = [
      {
        id: Date.now(),
        path: editingReward.value.pictureUrl,
        name: 'reward-image'
      }
    ]
  } else {
    initialImages.value = []
  }

  images.value = []
  deletedImages.value = []
}

const updateVoucher = (selected: number) => {
  const voucher = rewardVouchers.value.find((v) => v.id === selected)
  if (voucher) {
    editingReward.value.rewardType = {
      id: voucher.id,
      slug: voucher.slug,
      name: voucher.text,
      isVoucher: voucher.isVoucher
    }
  }
}

const handleNameChange = (value: string) => {
  resetErrors()
  emit('update:nameChanged', value)
}

const rewardTypeIdString = computed({
  get: () => String(editingReward.value.rewardType.id ?? ''),
  set: (v: string) => {
    editingReward.value.rewardType.id = Number(v)
    updateVoucher(Number(v))
  }
})

const rewardVoucherOptions = computed(() =>
  rewardVouchers.value.map((voucher) => ({ value: String(voucher.id), label: voucher.text }))
)

onMounted(async () => {
  await getMarketplacePartners()

  if (props.usage !== 'offer') {
    resetImage()
  }
})

defineExpose({
  resetImage: () => {
    if (props.usage === 'offer') {
      rewardInfosBlockRef.value?.resetImage()
    } else {
      resetImage()
    }
  },
  setNameError: () => {
    if (props.usage === 'offer') {
      rewardInfosBlockRef.value?.setNameError()
    } else {
      setNameErrorState()
    }
  }
})
</script>

<template>
  <div class="flex-1 lg:max-w-4xl">
    <div class="w-full flex flex-col gap-6">
      <ProductAssociationBlock
        v-if="showProductAssociation"
        v-model:productSkusCsv="editingReward.externalId"
        :is-loading="!!isLoadingPartners"
        :partners="rewardPartners ?? []"
        :associated-products="editingReward.items"
        help-url="https://heypongohelp.zendesk.com/hc/fr/articles/24713268234641-Comment-associer-des-r%C3%A9compenses-Pongo-%C3%A0-des-produits-de-mon-catalogue"
        @open-modal="openProductModal"
        @remove-product="removeAssociatedProduct"
        @configure="router.push({ name: 'IntegrationList' })"
      />

      <RewardTypeBlock v-if="usage === 'offer'" v-model:editing-reward="editingReward" />

      <RewardInfosBlock
        v-if="usage === 'offer'"
        ref="rewardInfosBlockRef"
        v-model:editing-reward="editingReward"
        @update:name-changed="handleNameChange"
      />

      <!-- Non-offer mode — bloc original inchangé -->
      <RewardFormBlock v-if="usage !== 'offer'" :title="t('pages.reward.details.title')">
        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.reward.details.typeLabel') }}</span>
          </label>

          <ComboboxSelect
            v-model="rewardTypeIdString"
            :options="rewardVoucherOptions"
            :placeholder="t('pages.reward.details.typeLabel')"
          />
        </div>

        <div v-if="isDisplayed('discountValuePercent')">
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.reward.details.discountValuePercentLabel') }}</span>
          </label>
          <AppFormInput
            id="discountValuePercent"
            v-model="editingReward.value"
            type="number"
            step="1"
            :min="0"
            :max="100"
            title="discountValuePercent"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground w-3 h-5 flex items-center justify-center">%</div>
            </template>
          </AppFormInput>
        </div>

        <div v-if="isDisplayed('voucherDiscountAmount')">
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.reward.details.voucherDiscountAmountLabel') }}</span>
          </label>
          <AppFormInput
            id="voucherDiscountAmount"
            v-model="editingReward.value"
            type="number"
            step="0.01"
            :min="0"
            title="voucherDiscountAmount"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground w-3 h-5 flex items-center justify-center">€</div>
            </template>
          </AppFormInput>
        </div>

        <div v-if="isDisplayed('minimumOrderAmount')">
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.reward.details.minimumOrderAmountLabel') }}</span>
          </label>
          <AppFormInput
            id="minimumOrderAmount"
            v-model="editingReward.minimumOrderAmount"
            type="number"
            step="0.01"
            :min="0"
            title="minimumOrderAmount"
          >
            <template #icon-right>
              <div class="text-sm text-muted-foreground w-3 h-5 flex items-center justify-center">€</div>
            </template>
          </AppFormInput>
        </div>

        <div ref="nameContainer">
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.reward.details.nameLabel') }}</span>
            <span class="text-red-500">*</span>
          </label>
          <AppFormInput
            id="reward-name"
            v-model="editingReward.name"
            class="w-full"
            :maxlength="REWARD_NAME_MAX_LENGTH"
            :errors="errors"
            error-classes="!mt-1 !text-xs"
            @update:model-value="handleNameChange"
          />
          <p v-if="errors.length === 0" class="text-xs text-muted-foreground mt-1">
            {{ editingReward.name.length }}/{{ REWARD_NAME_MAX_LENGTH }} {{ t('pages.reward.details.characters') }}
          </p>
        </div>

        <div>
          <label for="reward-description" class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.reward.details.descriptionLabel') }}</span>
            <Badge variant="secondary">{{ t('pages.reward.details.descriptionWebappOnly') }}</Badge>
          </label>
          <Textarea
            id="reward-description"
            v-model="rewardDescription"
            :placeholder="t('pages.reward.details.descriptionPlaceholder')"
            :rows="3"
            :maxlength="REWARD_DESCRIPTION_MAX_LENGTH"
            class="w-full text-sm"
          />
          <p class="text-xs text-muted-foreground mt-1">
            {{ rewardDescription.length }}/{{ REWARD_DESCRIPTION_MAX_LENGTH }}
            {{ t('pages.reward.details.characters') }}
          </p>
        </div>

        <div>
          <label class="mb-2 flex items-center gap-1 text-sm text-foreground">
            <span>{{ t('pages.reward.details.imageLabel') }}</span>
            <span class="text-red-500">*</span>
          </label>

          <ImageUploaderNew
            ref="imageUploader"
            :images="images"
            :initial-images="initialImages"
            :deleted-images="deletedImages"
            :single-image="true"
            :aspect-ratio="[1, 1]"
            @update:images="updateImages"
            @update:deleted-images="updateDeletedImages"
          />
        </div>
      </RewardFormBlock>

      <slot name="additional-form-blocks" />
    </div>
  </div>
  <div class="flex flex-col lg:w-sm w-full">
    <Card :class="['overflow-hidden sticky w-full', sideitemTopOffset]">
      <CardHeader>
        <CardTitle>{{ t('pages.reward.summary.title') }}</CardTitle>
      </CardHeader>

      <CardContent class="space-y-5">
        <div class="rounded-lg bg-muted p-4">
          <div class="flex items-center gap-3">
            <img
              v-if="editingReward.pictureUrl"
              :src="editingReward.pictureUrl"
              :alt="editingReward.name"
              class="h-11 w-11 shrink-0 rounded-lg object-cover"
            />
            <div v-else class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <BaseIcon name="HeroGiftIconOutline" class="h-5 w-5 text-primary" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium leading-snug text-foreground">
                {{ editingReward.name || '—' }}
              </p>
              <BaseChip class="mt-1" :icon="rewardTypeIcon" :text="rewardTypeLabel" />
            </div>
          </div>

          <div v-if="rewardSummaryItems.length > 0" class="mt-3 border-t border-border/80 pt-3">
            <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {{ t('pages.offer.create.summary.detailTitle') }}
            </p>
            <ul class="space-y-1">
              <li
                v-for="item in rewardSummaryItems"
                :key="`${item.icon}-${item.text}`"
                class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary"
              >
                <BaseIcon :name="item.icon as Icons" class="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span>
                  {{ item.text }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="usageTags?.length > 0" class="rounded-lg bg-muted p-4">
          <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.reward.summary.usage') }}
          </p>
          <RewardUsageTag :usage-tags="usageTags" />
        </div>

        <slot name="additional-summary" />
      </CardContent>
    </Card>
  </div>

  <PartnerProductModal
    v-if="isProductModalOpen"
    is-open
    :partner="selectedPartner"
    :associated-products="editingReward.items"
    @close="isProductModalOpen = false"
    @select="handleProductSelect"
    @remove="removeAssociatedProduct"
  />
</template>

<style scoped>
.partner-list-enter-active,
.partner-list-leave-active {
  transition: all 0.3s ease;
}

.partner-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.partner-list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.partner-list-move {
  transition: transform 0.3s ease;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
